// src/components/AboutSection.tsx
import React, { useState } from "react";
import ImageKoridor from "../assets/hakkimizda-koridor-hq.webp";
import ImageOda1 from "../assets/hakkimizda-tedavi-oda1-hq.webp";
import ImageBekleme from "../assets/hakkimizda-bekleme-alani-hq.webp";
import ImageOda2 from "../assets/hakkimizda-tedavi-oda2-hq.webp";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const thumbnails = [
  { src: ImageOda1, alt: "Tedavi Odası" },
  { src: ImageBekleme, alt: "Bekleme Alanı" },
  { src: ImageOda2, alt: "Tedavi Odası" },
];

const AboutSection: React.FC = () => {
  const { t } = useTranslation();
  const [showMore, setShowMore] = useState<boolean>(false);

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const extraInfo = [
    { titleKey: "about.missionTitle", textKey: "about.missionText" },
    { titleKey: "about.visionTitle", textKey: "about.visionText" },
    { titleKey: "about.valuesTitle", textKey: "about.valuesText" },
  ];

  return (
    <section className="bg-white max-w-7xl mx-auto px-6 py-20">
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="text-yellow-600 font-semibold text-sm uppercase tracking-widest text-center mb-3"
      >
        {t("about.eyebrow")}
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6 }}
        className="text-4xl font-bold mb-12 text-center"
      >
        {t("about.title")}
      </motion.h2>

      {/* Fotoğraf Galerisi - sabit mozaik düzeni, görsellerin gerçek çözünürlüğüne yakın boyutlarda */}
      <div className="max-w-5xl mx-auto mb-14">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="h-56 md:h-72 rounded-2xl overflow-hidden shadow-lg mb-3"
        >
          <img
            src={ImageKoridor}
            alt="Dt. Mert Sakarya Kliniği - Koridor"
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          />
        </motion.div>

        <div className="grid grid-cols-3 gap-3">
          {thumbnails.map((img, idx) => (
            <motion.div
              key={img.src}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: 0.1 + idx * 0.1 }}
              className="h-40 md:h-52 rounded-xl overflow-hidden shadow-md"
            >
              <img
                src={img.src}
                alt={`Dt. Mert Sakarya Kliniği - ${img.alt}`}
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Metin Kısmı */}
      <div className="flex flex-col gap-6 max-w-3xl mx-auto">
        <motion.p
          className="text-gray-700 text-center leading-relaxed"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          transition={{ duration: 0.6 }}
        >
          {t("about.paragraph1")}
        </motion.p>

        {/* Daha Fazla Bilgi Butonu */}
        <button
          onClick={() => setShowMore(!showMore)}
          className="bg-yellow-600 text-white px-6 py-3 rounded-lg shadow hover:bg-yellow-700 transition-colors w-max mx-auto flex items-center gap-2"
        >
          {showMore ? t("about.showLess") : t("about.showMore")}
          {showMore ? <FaChevronUp /> : <FaChevronDown />}
        </button>
      </div>

      {/* Açılan Kısım */}
      <AnimatePresence>
        {showMore && (
          <motion.div
            className="mt-12 max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6 }}
          >
            {extraInfo.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.15 }}
                className="text-center md:text-left"
              >
                <h3 className="text-xl font-bold mb-2">{t(item.titleKey)}</h3>
                <p className="text-gray-700 leading-relaxed">{t(item.textKey)}</p>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default AboutSection;
